/**
 * Marketing Results Panel.
 *
 * Renders:
 *   - Pivot mode: KPI summary cards + sortable data table with ROAS highlighting + optional transpose
 *   - Time Series mode: interactive Recharts line chart + data table below
 *
 * Ported visualisation parity from the Python Dash version.
 */
import { useMemo, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  BarChart3,
  Play,
  AlertCircle,
  TrendingUp,
  DollarSign,
  RotateCcw,
  Users,
  Search,
  CheckSquare,
  Square,
  Filter,
  X,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonChart } from '@/components/ui/Skeleton';
import { MarketingPivotTable } from '@/components/charts/MarketingPivotTable';
import { fadeInUp } from '@/design/animations';
import { colors } from '@/design/tokens';
import { cn } from '@/lib/cn';

interface MarketingResultsPanelProps {
  chartType: 'ts' | 'pivot';
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: unknown;
  hasRun: boolean;
  canRun: boolean;
  onRun: () => void;
  selectedMetrics: string[];
  /** Comparison period data for pivot (same shape as main data, different date range) */
  comparisonData?: Record<string, unknown>;
}

// ─── Metric colour palette (matches Python METRIC_COLORS) ───────────────────

const METRIC_COLORS: Record<string, string> = {
  cost: '#EF4444',
  revenue: '#10B981',
  refunds: '#F43F5E',
  refund_rate: '#E11D48',
  installs: '#3B82F6',
  total_purchases: '#F59E0B',
  initial_purchases: '#22D3EE',
  renewals: '#A855F7',
  special_offer_purchases: '#FF6B6B',
  ltv: '#8B5CF6',
  roas: '#EC4899',
  cpi: '#14B8A6',
  impressions: '#6366F1',
  clicks: '#F97316',
  cvr: '#06B6D4',
  pcvr: '#84CC16',
  cpp: '#64748B',
  landing_page_visits: '#A78BFA',
  landing_page_clicks: '#34D399',
};

const METRIC_FORMAT: Record<string, (v: number) => string> = {
  cost: (v) => `₺${formatNum(v)}`,
  revenue: (v) => `₺${formatNum(v)}`,
  refunds: (v) => `₺${formatNum(v)}`,
  refund_rate: (v) => `${v.toFixed(1)}%`,
  cpi: (v) => `₺${v.toFixed(2)}`,
  cpp: (v) => `₺${v.toFixed(2)}`,
  roas: (v) => v.toFixed(2),
  ltv: (v) => `₺${v.toFixed(2)}`,
  cvr: (v) => `${v.toFixed(1)}%`,
  pcvr: (v) => `${v.toFixed(1)}%`,
  impressions: (v) => formatNum(v),
  clicks: (v) => formatNum(v),
  installs: (v) => formatNum(v),
  total_purchases: (v) => formatNum(v),
  initial_purchases: (v) => formatNum(v),
  renewals: (v) => formatNum(v),
  special_offer_purchases: (v) => formatNum(v),
  landing_page_visits: (v) => formatNum(v),
  landing_page_clicks: (v) => formatNum(v),
};

const METRIC_LABELS: Record<string, string> = {
  cost: 'Cost (₺)',
  revenue: 'Revenue (₺)',
  refunds: 'Refunds (₺)',
  refund_rate: 'Refund Rate (%)',
  impressions: 'Ad Impressions',
  clicks: 'Ad Clicks',
  installs: 'Installs',
  cpi: 'CPI',
  roas: 'ROAS',
  ltv: 'LTV',
  cvr: 'CVR (%)',
  pcvr: 'PCVR (%)',
  cpp: 'CPP',
  total_purchases: 'Purchases',
  initial_purchases: 'Initial',
  renewals: 'Renewals',
  special_offer_purchases: 'Special Offers',
  landing_page_visits: 'Landing Page Visits',
  landing_page_clicks: 'Landing Page Clicks',
};

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function formatMetric(key: string, value: number): string {
  const formatter = METRIC_FORMAT[key];
  if (formatter) return formatter(value);
  return value % 1 === 0 ? String(value) : value.toFixed(2);
}

function metricToRowKey(metric: string): string {
  const map: Record<string, string> = {
    cost: 'cost_try',
    revenue: 'total_revenue_try',
    refunds: 'refunds_try',
  };
  return map[metric] ?? metric;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function MarketingResultsPanel({
  chartType,
  isLoading,
  isError,
  error,
  data,
  hasRun,
  canRun,
  onRun,
  selectedMetrics,
  comparisonData,
}: MarketingResultsPanelProps) {
  // Keep a ref to the last successful data so we can keep the chart mounted
  // (and preserve picker state) while a re-query is loading.
  const [lastData, setLastData] = useState<unknown>(null);
  const [lastChartType, setLastChartType] = useState<'ts' | 'pivot'>(chartType);
  const [lastMetrics, setLastMetrics] = useState<string[]>(selectedMetrics);
  const [lastComparisonData, setLastComparisonData] = useState<Record<string, unknown> | undefined>(undefined);

  useEffect(() => {
    if (data && !isLoading) {
      setLastData(data);
      setLastChartType(chartType);
      setLastMetrics(selectedMetrics);
      setLastComparisonData(comparisonData);
    }
  }, [data, isLoading, chartType, selectedMetrics, comparisonData]);

  // Initial load with no previous data — show skeleton
  if (isLoading && !lastData) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <SkeletonChart />
      </motion.div>
    );
  }

  // First run — no previous data to show
  if (!hasRun && !lastData) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Ready to analyze"
        description="Configure your marketing query and click Run to see results"
        action={
          canRun && (
            <Button icon={<Play />} onClick={onRun}>
              Run Query
            </Button>
          )
        }
      />
    );
  }

  // Error with no previous data
  if (isError && !lastData) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="border-red-200/80">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-900 mb-0.5">Query Error</h3>
              <p className="text-xs text-red-600">{error?.message || 'An unexpected error occurred'}</p>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }

  // Use current data if available, otherwise keep showing last successful data
  const displayData = (!isLoading && data) ? data : lastData;
  const displayChartType = (!isLoading && data) ? chartType : lastChartType;
  const displayMetrics = (!isLoading && data) ? selectedMetrics : lastMetrics;

  return (
    <div className="relative">
      {/* Loading overlay — keeps the chart mounted underneath */}
      {isLoading && (
        <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-[1px] rounded-2xl flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-sm border border-slate-200">
            <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-medium text-slate-600">Updating...</span>
          </div>
        </div>
      )}

      {/* Error banner — shown on top of existing results */}
      {isError && (
        <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mb-4">
          <Card className="border-red-200/80">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-900 mb-0.5">Query Error</h3>
                <p className="text-xs text-red-600">{error?.message || 'An unexpected error occurred'}</p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {displayChartType === 'pivot' ? (
        <MarketingPivotResults
          data={displayData}
          selectedMetrics={displayMetrics}
          comparisonData={(!isLoading && data) ? comparisonData : lastComparisonData}
        />
      ) : (
        <MarketingTimeSeriesResults
          data={displayData}
          selectedMetrics={displayMetrics}
          comparisonData={(!isLoading && data) ? comparisonData : lastComparisonData}
        />
      )}
    </div>
  );
}

// ─── Pivot Results ───────────────────────────────────────────────────────────

function MarketingPivotResults({
  data,
  selectedMetrics,
  comparisonData,
}: {
  data: unknown;
  selectedMetrics: string[];
  comparisonData?: Record<string, unknown>;
}) {
  const parsed = data as {
    rows?: Array<Record<string, unknown>>;
    rowBreakdown?: string;
    summary?: { totalCost: number; totalRevenue: number; totalRefunds?: number; totalInstalls: number; overallRoas: number };
    metrics?: string[];
  } | null;

  const rows = parsed?.rows ?? [];
  const breakdownKey = parsed?.rowBreakdown ?? 'campaign_name';
  const summary = parsed?.summary;
  const displayMetrics = selectedMetrics.length > 0 ? selectedMetrics : (parsed?.metrics ?? []);

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={BarChart3}
          title="No data"
          description="No marketing data available for the selected configuration"
        />
      </Card>
    );
  }

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="space-y-4">
      {/* Summary KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard
            label="Total Cost"
            value={`₺${formatNum(summary.totalCost)}`}
            icon={<DollarSign className="w-4 h-4" />}
            color="rose"
          />
          <KpiCard
            label="Total Revenue"
            value={`₺${formatNum(summary.totalRevenue)}`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="emerald"
          />
          <KpiCard
            label="Refunds"
            value={`₺${formatNum(summary.totalRefunds ?? 0)}`}
            icon={<RotateCcw className="w-4 h-4" />}
            color="orange"
          />
          <KpiCard
            label="Total Installs"
            value={formatNum(summary.totalInstalls)}
            icon={<Users className="w-4 h-4" />}
            color="sky"
          />
          <KpiCard
            label="Overall ROAS"
            value={summary.overallRoas.toFixed(2)}
            icon={<BarChart3 className="w-4 h-4" />}
            color="amber"
          />
        </div>
      )}

      {/* Data Table — reusable MarketingPivotTable */}
      <MarketingPivotTable
        rows={rows}
        breakdownKey={breakdownKey}
        displayMetrics={displayMetrics}
        comparisonRows={(comparisonData as { rows?: Array<Record<string, unknown>> } | undefined)?.rows}
      />
    </motion.div>
  );
}

// ─── Default number of breakdown values to select initially ──────────────────

const DEFAULT_INITIAL_SELECTION = 10;

// ─── Time Series Results ─────────────────────────────────────────────────────

function MarketingTimeSeriesResults({
  data,
  selectedMetrics,
  comparisonData,
}: {
  data: unknown;
  selectedMetrics: string[];
  comparisonData?: Record<string, unknown>;
}) {
  const parsed = data as {
    points?: Array<Record<string, unknown>>;
    metrics?: string[];
    cumulative?: boolean;
    rowBreakdown?: string;
  } | null;

  const points = parsed?.points ?? [];
  const displayMetrics = selectedMetrics.length > 0 ? selectedMetrics : (parsed?.metrics ?? []);
  const hasBreakdown = parsed?.rowBreakdown && points.some((p) => p.breakdown_value !== undefined);
  const [breakdownMetric, setBreakdownMetric] = useState<string>(displayMetrics[0] ?? 'installs');
  const [pickerSearch, setPickerSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  // Sync breakdownMetric when displayMetrics changes and current selection is
  // no longer available (e.g. user switched from cost+revenue to just revenue)
  useEffect(() => {
    if (displayMetrics.length > 0 && !displayMetrics.includes(breakdownMetric)) {
      setBreakdownMetric(displayMetrics[0]);
    }
  }, [displayMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Compute all breakdown values ranked by selected metric total ─────
  const isCumulative = !!parsed?.cumulative;
  const { allBreakdownKeys, totalByKey } = useMemo(() => {
    if (!hasBreakdown) return { allBreakdownKeys: [] as string[], totalByKey: new Map<string, number>() };

    const rawKey = metricToRowKey(breakdownMetric);
    const totalByBd = new Map<string, number>();

    if (isCumulative) {
      // Cumulative data: each point is a running total, so the last
      // (chronologically latest) value per breakdown IS the true total.
      // We keep track of the latest time_bucket per breakdown value.
      const latestBucket = new Map<string, string>();
      for (const point of points) {
        const bdValue = String(point.breakdown_value ?? 'Unknown');
        const tb = String(point.time_bucket ?? '');
        const prev = latestBucket.get(bdValue);
        if (!prev || tb > prev) {
          latestBucket.set(bdValue, tb);
          totalByBd.set(bdValue, Math.abs(Number(point[rawKey] ?? 0)));
        }
      }
    } else {
      // Per-period data: sum across all time buckets.
      for (const point of points) {
        const bdValue = String(point.breakdown_value ?? 'Unknown');
        totalByBd.set(bdValue, (totalByBd.get(bdValue) ?? 0) + Math.abs(Number(point[rawKey] ?? 0)));
      }
    }

    const sorted = Array.from(totalByBd.entries()).sort((a, b) => b[1] - a[1]);
    return { allBreakdownKeys: sorted.map(([k]) => k), totalByKey: totalByBd };
  }, [points, hasBreakdown, breakdownMetric, isCumulative]);

  // ─── Selected breakdown values (visible on chart) ─────────────────────
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [initializedForData, setInitializedForData] = useState<string | null>(null);

  // Initialize selection only once when the set of breakdown values changes
  // (i.e. a new query was run that returned different breakdown values).
  // Sort alphabetically for the fingerprint so metric re-ranking doesn't
  // cause a false change.
  const breakdownKeyFingerprint = useMemo(
    () => allBreakdownKeys.length > 0 ? [...allBreakdownKeys].sort().join('\0') : null,
    [allBreakdownKeys],
  );

  useEffect(() => {
    if (breakdownKeyFingerprint && breakdownKeyFingerprint !== initializedForData) {
      setSelectedKeys(new Set(allBreakdownKeys.slice(0, DEFAULT_INITIAL_SELECTION)));
      setInitializedForData(breakdownKeyFingerprint);
    }
  }, [breakdownKeyFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBreakdownKey = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedKeys(new Set(allBreakdownKeys));
  }, [allBreakdownKeys]);

  const deselectAll = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  // ─── Stable color assignment: map each breakdown key to a color index ──
  const colorMap = useMemo(() => {
    const map = new Map<string, string>();
    allBreakdownKeys.forEach((key, i) => {
      map.set(key, colors.chart[i % colors.chart.length]);
    });
    return map;
  }, [allBreakdownKeys]);

  // ─── Normal mode (no breakdown): one line per metric ─────────────────
  const normalChartData = useMemo(() => {
    if (hasBreakdown) return [];
    return points.map((point) => {
      const timeBucket = String(point.time_bucket ?? '');
      const label = formatTimeBucketLabel(timeBucket);
      const row: Record<string, string | number> = { date: label, _raw_ts: timeBucket };
      for (const m of displayMetrics) {
        const rawKey = metricToRowKey(m);
        row[m] = Number(point[rawKey] ?? 0);
      }
      return row;
    });
  }, [points, displayMetrics, hasBreakdown]);

  // ─── Breakdown mode: build chart data for all keys ────────────────────
  const breakdownChartData = useMemo(() => {
    if (!hasBreakdown) return [] as Record<string, string | number>[];

    const rawKey = metricToRowKey(breakdownMetric);
    const dateMap = new Map<string, Record<string, string | number>>();

    for (const point of points) {
      const timeBucket = String(point.time_bucket ?? '');
      const label = formatTimeBucketLabel(timeBucket);
      const bdValue = String(point.breakdown_value ?? 'Unknown');
      const val = Number(point[rawKey] ?? 0);

      if (!dateMap.has(timeBucket)) {
        dateMap.set(timeBucket, { date: label, _raw_ts: timeBucket });
      }
      const row = dateMap.get(timeBucket)!;
      row[bdValue] = val;
    }

    return Array.from(dateMap.values()).sort((a, b) =>
      String(a._raw_ts).localeCompare(String(b._raw_ts)),
    );
  }, [points, hasBreakdown, breakdownMetric]);

  // ─── Filtered picker items ────────────────────────────────────────────
  const filteredPickerKeys = useMemo(() => {
    if (!pickerSearch.trim()) return allBreakdownKeys;
    const q = pickerSearch.toLowerCase();
    return allBreakdownKeys.filter((k) => k.toLowerCase().includes(q));
  }, [allBreakdownKeys, pickerSearch]);

  // ─── Comparison data processing ──────────────────────────────────────
  const { mergedChartData, cmpLineKeys } = useMemo(() => {
    const mainData = hasBreakdown ? breakdownChartData : normalChartData;
    const mainKeys = hasBreakdown
      ? allBreakdownKeys.filter((k) => selectedKeys.has(k))
      : displayMetrics;

    if (!comparisonData || mainData.length === 0) {
      return { mergedChartData: mainData, cmpLineKeys: [] as string[] };
    }

    const cmpParsed = comparisonData as {
      points?: Array<Record<string, unknown>>;
    } | null;
    const cmpPoints = cmpParsed?.points ?? [];
    if (cmpPoints.length === 0) return { mergedChartData: mainData, cmpLineKeys: [] as string[] };

    const cmpHasBreakdown = cmpPoints.some((p) => p.breakdown_value !== undefined);

    if (!hasBreakdown || !cmpHasBreakdown) {
      // Normal mode: one cmp line per metric
      const cmpSorted = [...cmpPoints].sort((a, b) =>
        String(a.time_bucket ?? '').localeCompare(String(b.time_bucket ?? '')),
      );
      const merged = mainData.map((point, i) => {
        const cmpPoint = cmpSorted[i];
        const extra: Record<string, number | undefined> = {};
        for (const m of mainKeys) {
          const rawKey = metricToRowKey(m);
          extra[`${m}__cmp`] = cmpPoint ? Number(cmpPoint[rawKey] ?? 0) : undefined;
        }
        return { ...point, ...extra };
      });
      return { mergedChartData: merged, cmpLineKeys: mainKeys.map((k) => `${k}__cmp`) };
    }

    // Breakdown mode: build cmp data grouped by date, then align by index
    const cmpRawKey = metricToRowKey(breakdownMetric);
    const cmpDateMap = new Map<string, Record<string, number>>();
    for (const point of cmpPoints) {
      const timeBucket = String(point.time_bucket ?? '');
      const bdValue = String(point.breakdown_value ?? 'Unknown');
      const val = Number(point[cmpRawKey] ?? 0);
      if (!cmpDateMap.has(timeBucket)) cmpDateMap.set(timeBucket, {});
      cmpDateMap.get(timeBucket)![bdValue] = val;
    }
    const cmpRows = Array.from(cmpDateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, vals]) => vals);

    const merged = mainData.map((point, i) => {
      const cmpRow = cmpRows[i];
      const extra: Record<string, number | undefined> = {};
      for (const key of mainKeys) {
        extra[`${key}__cmp`] = cmpRow ? cmpRow[key] : undefined;
      }
      return { ...point, ...extra };
    });

    return { mergedChartData: merged, cmpLineKeys: mainKeys.map((k) => `${k}__cmp`) };
  }, [
    hasBreakdown, breakdownChartData, normalChartData,
    allBreakdownKeys, selectedKeys, displayMetrics, breakdownMetric,
    comparisonData,
  ]);

  if (points.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={BarChart3}
          title="No data"
          description="No time series data available for the selected configuration"
        />
      </Card>
    );
  }

  const chartData = hasBreakdown ? breakdownChartData : normalChartData;
  const lineKeys = hasBreakdown
    ? allBreakdownKeys.filter((k) => selectedKeys.has(k))
    : displayMetrics;
  const hasCmp = cmpLineKeys.length > 0;

  const breakdownLabel = parsed?.rowBreakdown?.replace(/_/g, ' ') ?? 'breakdown';

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="space-y-4">
      {/* Chart */}
      <Card>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Marketing Time Series{parsed?.cumulative ? ' (Cumulative)' : ' (Per Period)'}
          </h3>
          <div className="flex items-center gap-2">
            {/* Breakdown picker trigger */}
            {hasBreakdown && allBreakdownKeys.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <Filter className="w-3.5 h-3.5" />
                <span className="capitalize">{breakdownLabel}</span>
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-600 text-[10px] font-semibold tabular-nums">
                  {selectedKeys.size}/{allBreakdownKeys.length}
                </span>
              </button>
            )}
            {/* Breakdown metric selector */}
            {hasBreakdown && (
              <select
                value={breakdownMetric}
                onChange={(e) => setBreakdownMetric(e.target.value)}
                className="h-7 px-2 rounded-md border border-slate-200 bg-white text-xs"
              >
                {displayMetrics.map((m) => (
                  <option key={m} value={m}>{METRIC_LABELS[m] ?? m}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={hasCmp ? mergedChartData : chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              tickLine={false}
              axisLine={{ stroke: '#E2E8F0' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              tickLine={false}
              axisLine={false}
              width={70}
              tickFormatter={(v) => formatNum(v)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 12px -4px rgb(0 0 0 / 0.08)',
                padding: '8px 12px',
                fontSize: '12px',
                maxHeight: '320px',
                overflowY: 'auto',
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              itemSorter={(item: any) => -(Number(item.value ?? 0))}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={((value: any, name: any) => {
                const nameStr = String(name);
                const isCmpLine = nameStr.endsWith('__cmp');
                const baseName = isCmpLine ? nameStr.replace(/__cmp$/, '') : nameStr;
                const metricKey = hasBreakdown ? breakdownMetric : baseName;
                const displayName = isCmpLine
                  ? `${hasBreakdown ? baseName : (METRIC_LABELS[baseName] ?? baseName)} (cmp)`
                  : (hasBreakdown ? baseName : (METRIC_LABELS[baseName] ?? baseName));
                return [formatMetric(metricKey, Number(value ?? 0)), displayName];
              }) as any}
            />
            {/* Main lines */}
            {lineKeys.map((key) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={hasBreakdown ? (colorMap.get(key) ?? '#94A3B8') : (METRIC_COLORS[key] ?? colors.chart[0])}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                connectNulls
              />
            ))}
            {/* Comparison lines — dashed, lower opacity, same color */}
            {hasCmp && lineKeys.map((key) => (
              <Line
                key={`${key}__cmp`}
                type="monotone"
                dataKey={`${key}__cmp`}
                stroke={hasBreakdown ? (colorMap.get(key) ?? '#94A3B8') : (METRIC_COLORS[key] ?? colors.chart[0])}
                strokeWidth={1.5}
                strokeDasharray="6 3"
                strokeOpacity={0.4}
                dot={false}
                activeDot={{ r: 3, strokeWidth: 0, fillOpacity: 0.4 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Legend below chart */}
        {(lineKeys.length > 1 || hasCmp) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-slate-100">
            {lineKeys.map((key) => {
              const color = hasBreakdown
                ? (colorMap.get(key) ?? '#94A3B8')
                : (METRIC_COLORS[key] ?? colors.chart[0]);
              return (
                <div key={key} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-xs text-slate-500">
                      {hasBreakdown ? key : (METRIC_LABELS[key] ?? key)}
                    </span>
                  </div>
                  {hasCmp && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-0 border-t border-dashed shrink-0" style={{ borderColor: color, opacity: 0.5 }} />
                      <span className="text-xs text-slate-400">cmp</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ─── Breakdown Picker Dialog ─────────────────────────────────────── */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
            onClick={() => setShowPicker(false)}
          />
          {/* Dialog */}
          <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Dialog header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 capitalize">
                  Select {breakdownLabel}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {selectedKeys.size} of {allBreakdownKeys.length} selected
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search + actions */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-50 bg-slate-50/50">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder={`Search ${breakdownLabel}...`}
                  className="w-full h-8 pl-8 pr-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={selectAll}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-white transition-colors whitespace-nowrap"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                Select All
              </button>
              <button
                type="button"
                onClick={deselectAll}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-white transition-colors whitespace-nowrap"
              >
                <Square className="w-3.5 h-3.5" />
                Deselect All
              </button>
            </div>

            {/* Scrollable grid */}
            <div className="max-h-80 overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-100">
                {filteredPickerKeys.map((key) => {
                  const isSelected = selectedKeys.has(key);
                  const color = colorMap.get(key) ?? '#94A3B8';
                  const total = totalByKey.get(key) ?? 0;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleBreakdownKey(key)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2.5 text-left transition-colors bg-white hover:bg-slate-50 overflow-hidden',
                        !isSelected && 'opacity-40',
                      )}
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: isSelected ? color : '#CBD5E1' }}
                      />
                      <span className="text-xs font-medium text-slate-700 truncate">{key}</span>
                      <span className="text-[10px] text-slate-400 tabular-nums shrink-0 ml-auto">
                        {formatMetric(breakdownMetric, total)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {filteredPickerKeys.length === 0 && (
                <div className="py-8 text-center text-xs text-slate-400">No matches</div>
              )}
            </div>

            {/* Dialog footer */}
            <div className="flex items-center justify-end px-5 py-3 border-t border-slate-100 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

    </motion.div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTimeBucketLabel(timeBucket: string): string {
  try {
    const d = new Date(timeBucket);
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleString('en-US', { month: 'short' });
      const day = d.getDate();
      // If the raw string contains a time component (T or space + digits),
      // always show the hour — including midnight (00:00).
      const hasTime = timeBucket.includes('T') || /\d{4}-\d{2}-\d{2} \d{2}/.test(timeBucket);
      if (hasTime) {
        const hours = d.getHours();
        return `${month} ${day} ${String(hours).padStart(2, '0')}:00`;
      }
      return `${month} ${day}`;
    }
  } catch { /* keep raw */ }
  return timeBucket;
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KPI_COLORS: Record<string, string> = {
  rose: 'bg-rose-50 text-rose-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  sky: 'bg-sky-50 text-sky-600',
  amber: 'bg-amber-50 text-amber-600',
  orange: 'bg-orange-50 text-orange-600',
};

function KpiCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card className="!py-3 !px-4">
      <div className="flex items-center gap-3">
        <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', KPI_COLORS[color])}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-lg font-bold text-slate-900 tabular-nums">{value}</p>
        </div>
      </div>
    </Card>
  );
}
